/**
 * The village animals (data/animals.ts): hens, chicks, ducks, cats, dogs and the field buffalo, by the hour and the
 * weather. Each lives round a home spot (where it spawned):
 *
 * - **Night:** home to sleep (cats are up and about instead, roaming wider).
 * - **Rain or a storm:** home to sit it out (ducks and the buffalo carry on).
 * - **Noon:** cats and dogs lie in the sun.
 * - **Otherwise:** pastimes by weight (eat, scratch, stand about, walk somewhere near home) and a call now and then.
 * - **A fight near:** they run from it. Hens, chicks and ducks also scatter from anyone who comes too close.
 * - **Chicks** keep close to the nearest hen.
 * - **Dogs** take to the player by temper (`dogTemper`): a friendly dog follows a while and barks for joy, a wary one
 *   keeps its distance and barks.
 *
 * Their state is the critter brain's `animal` (`AnimalMind`); `act` is what the renderer plays. Their choices draw
 * from a stream of their own (a hash of the animal and a counter), not the World's `Rng`, so the village animals
 * leave every other draw of the world as it was. Spawned in `setup` (after the zone's folk and critters).
 */
import { ANIMAL_SPAWNS, ANIMALS, type AnimalAct, type AnimalKind, type DogTemper } from '../data/animals';
import { freeAt, nearestFloor } from '../path';
import type { Entity, World } from '../world';
import type { SystemHost, WorldSystem } from './types';

export interface AnimalMind {
  act: AnimalAct;
  /** Seconds left of the act (or of the walk to `tx`, `ty`). */
  t: number;
  tx: number | null;
  ty: number | null;
  /** Home: where it sleeps and sits out the rain; it roams round it. */
  hx: number;
  hy: number;
  /** Its own random stream's counter. */
  seq: number;
  /** Until the next call (s). */
  callT: number;
  /** A dog: the player it is with, and how long more it keeps at it. */
  follow: number | null;
  followT: number;
  /** Until a dog takes to the player again after leaving off (s). */
  restT: number;
}

/** A spot of a fight this step: where blows are flying. */
interface Fight {
  x: number;
  y: number;
}

const isAnimal = (k: string): k is AnimalKind => k in ANIMALS;

/**
 * How a dog takes the player: wary of a wicked name (karma), or of someone the village remembers badly (the NPC
 * memory's village score, `progress.npcMemory.town`, when it is there).
 */
export function dogTemper(karma: string, town = 0): DogTemper {
  return karma === 'evil' || karma === 'mad' || town <= -20 ? 'wary' : 'friendly';
}

/** The village's memory of the player (systems/npcMemory.ts), read only if the progress carries it. */
const townScore = (w: World): number =>
  (w.progress as { npcMemory?: { town?: Record<string, number> } }).npcMemory?.town?.[w.map.zone] ?? 0;

export class AnimalsSystem implements WorldSystem {
  readonly name = 'animals';
  private readonly fights: Fight[] = [];

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const w = this.h.world;
    const m = w.map;
    for (const s of ANIMAL_SPAWNS) {
      if (s.zone !== m.zone) continue;
      const base = s.region
        ? m.w > s.region[0] && m.h > s.region[1]
          ? { x: s.region[0], y: s.region[1] }
          : null
        : s.at === 'plaza'
          ? m.plaza
          : m.landmarks?.[s.at ?? ''];
      if (!base) continue;
      const info = ANIMALS[s.kind];
      for (let k = 0; k < s.n; k++) {
        // A spot round the base, fixed by the spawn and the count (no draw from the World's stream).
        const a = (k * 2.39996 + s.kind.length) % (Math.PI * 2);
        const r = k === 0 ? 0 : 0.5 + 0.35 * k;
        let x = base.x + (s.dx ?? 0) + Math.cos(a) * r;
        let y = base.y + (s.dy ?? 0) + Math.sin(a) * r;
        if (!freeAt(m, x, y)) {
          const alt = nearestFloor(m, x, y, 4);
          if (!alt) continue;
          [x, y] = alt;
        }
        const e = this.h.add({
          kind: 'ambient',
          x,
          y,
          name: '',
          title: '',
          sprite: info.card.sprite,
          scale: info.card.scale,
          speed: info.walk,
          radius: info.radius,
          facing: k % 2 ? 1 : -1,
        });
        e.critter = { kind: s.kind, cx: x, cy: y, state: 'idle', t: 0, vx: 0, vy: 0 };
      }
    }
  }

  /** Where blows are flying this step (read by `thinkCritter` in the World's next pass over its entities). */
  step(): void {
    const f = this.fights;
    f.length = 0;
    for (const e of this.h.world.entities.values()) {
      if (e.dead || (e.kind !== 'player' && e.kind !== 'monster' && e.kind !== 'rival')) continue;
      if (e.cast || e.swingT > 0 || e.hitT > 0) f.push({ x: e.x, y: e.y });
    }
  }

  /** A critter's thinking, when it is one of the village animals (else the World's own critter rules run). */
  thinkCritter(e: Entity, dt: number): boolean {
    const c = e.critter;
    if (!c || !isAnimal(c.kind)) return false;
    c.t -= dt;
    const a = (c.animal ??= this.mind(e));
    this.live(e, c.kind, a, dt);
    return true;
  }

  private mind(e: Entity): AnimalMind {
    const c = e.critter!;
    return {
      act: 'idle',
      t: 1 + (e.id % 5) * 0.4,
      tx: null,
      ty: null,
      hx: c.cx,
      hy: c.cy,
      seq: 0,
      callT: 4 + (e.id % 11),
      follow: null,
      followT: 0,
      restT: 0,
    };
  }

  /** The animal's own stream: a number in [0, 1), the next one each call. */
  private rand(e: Entity, a: AnimalMind): number {
    let x = (Math.imul(e.id, 0x9e3779b1) ^ Math.imul(++a.seq, 0x85ebca6b)) >>> 0;
    x = Math.imul(x ^ (x >>> 16), 0x7feb352d) >>> 0;
    x = Math.imul(x ^ (x >>> 15), 0x846ca68b) >>> 0;
    return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
  }

  private live(e: Entity, kind: AnimalKind, a: AnimalMind, dt: number): void {
    const w = this.h.world;
    const info = ANIMALS[kind];
    a.t -= dt;
    a.callT -= dt;
    a.restT = Math.max(0, a.restT - dt);
    e.moving = false;
    // A fight near: away from it, at a run.
    const fight = this.nearest(e, info.fight);
    if (fight) return this.flee(e, a, fight, info.run, dt, 1.2);
    // Too close to someone: hens, chicks and ducks scatter (and a cat from a child).
    const p = w.player;
    if (info.shy > 0 && !p.dead && Math.hypot(p.x - e.x, p.y - e.y) < info.shy)
      return this.flee(e, a, p, info.run, dt, 0.8);
    if (a.act === 'flee' && a.t > 0) return this.flee(e, a, null, info.run, dt, 0);
    // A dog and the player.
    if (kind === 'dog' && this.dog(e, a, info.walk, info.run, dt)) return;
    const rain = (() => {
      const k = w.weather.kind;
      return (k === 'rain' || k === 'storm') && w.weather.blend > 0.5;
    })();
    const night = w.dayPart === 'night';
    const home = (night && !info.nocturnal) || (rain && info.shelters);
    if (home) {
      // Home, then asleep (at night) or sitting it out (in the rain).
      if (this.goTo(e, a, a.hx, a.hy, info.walk * (rain ? 1.6 : 1), dt, 0.5)) return;
      this.still(e, a, night ? 'sleep' : kind === 'dog' || kind === 'cat' ? 'lie' : 'sit');
      return;
    }
    // Noon sun: cats and dogs stretch out where they are.
    if (info.basks && w.period.id === 'noon' && !rain && w.weather.kind !== 'fog') {
      this.still(e, a, 'lie', 30);
      return;
    }
    // Chicks keep close to a hen.
    if (kind === 'chick' && this.toHen(e, a, info.walk, info.run, dt)) return;
    // Out and about: on with the act, or a new one.
    if (a.act === 'walk' && a.tx !== null) {
      if (a.t > 0 && this.goTo(e, a, a.tx, a.ty!, info.walk, dt, 0.25)) return;
      a.tx = a.ty = null;
      a.t = 0;
    } else if (a.act === 'run' && a.tx !== null) {
      if (a.t > 0 && this.goTo(e, a, a.tx, a.ty!, info.run, dt, 0.4)) return;
      a.tx = a.ty = null;
      a.t = 0;
    }
    if (a.t > 0) return;
    if (a.callT <= 0) {
      a.callT = info.callEvery * (0.5 + this.rand(e, a));
      return this.still(e, a, 'call', 1.2);
    }
    this.pastime(e, kind, a, night);
  }

  private pastime(e: Entity, kind: AnimalKind, a: AnimalMind, night: boolean): void {
    const info = ANIMALS[kind];
    const [we, ws, wi, ww] = info.pastimes;
    const total = we + ws + wi + ww;
    const r = this.rand(e, a) * total;
    const t = 2 + this.rand(e, a) * 4;
    if (r < we) return this.still(e, a, 'eat', t);
    if (r < we + ws) return this.still(e, a, 'scratch', Math.min(t, 2.5));
    if (r < we + ws + wi) return this.still(e, a, kind === 'cat' || kind === 'dog' ? 'sit' : 'idle', t);
    // A walk somewhere near home (a cat's night rounds go wider, now and then at a trot).
    const roam = info.roam * (night && info.nocturnal ? 2.2 : 1);
    const ang = this.rand(e, a) * Math.PI * 2;
    const d = roam * Math.sqrt(this.rand(e, a));
    const tx = a.hx + Math.cos(ang) * d;
    const ty = a.hy + Math.sin(ang) * d;
    if (!freeAt(this.h.world.map, tx, ty, e.radius)) return this.still(e, a, 'idle', 1);
    a.tx = tx;
    a.ty = ty;
    a.act = kind === 'cat' && night && this.rand(e, a) < 0.25 ? 'run' : 'walk';
    a.t = 8;
  }

  /** Stands (sits, lies …) where it is for `t` seconds (or on until something else comes up). */
  private still(e: Entity, a: AnimalMind, act: AnimalAct, t?: number): void {
    if (a.act !== act) a.t = t ?? 1;
    else if (t !== undefined && a.t <= 0) a.t = t;
    a.act = act;
    a.tx = a.ty = null;
    e.moving = false;
  }

  /** Walks (or runs) toward a spot; false once there (within `near`) or stuck. */
  private goTo(
    e: Entity,
    a: AnimalMind,
    x: number,
    y: number,
    speed: number,
    dt: number,
    near: number,
  ): boolean {
    const dx = x - e.x;
    const dy = y - e.y;
    const d = Math.hypot(dx, dy);
    if (d <= near) return false;
    const s = Math.min(d, speed * dt);
    const fast = speed > ANIMALS[e.critter!.kind as AnimalKind].walk * 1.5;
    if (!this.h.move(e, (dx / d) * s, (dy / d) * s)) {
      a.act = 'idle';
      a.t = 0.8;
      a.tx = a.ty = null;
      return false;
    }
    if (a.act !== 'walk' && a.act !== 'run') a.t = 8;
    a.act = fast ? 'run' : 'walk';
    e.moving = true;
    return true;
  }

  /** Away from `from` (or on the way it was going), darting a little, for a while. */
  private flee(
    e: Entity,
    a: AnimalMind,
    from: { x: number; y: number } | null,
    speed: number,
    dt: number,
    t: number,
  ): void {
    if (from) {
      const dx = e.x - from.x;
      const dy = e.y - from.y;
      const l = Math.hypot(dx, dy) || 1;
      a.tx = dx / l;
      a.ty = dy / l;
      if (a.act !== 'flee' || a.t < t) a.t = t;
    }
    a.act = 'flee';
    const ux = a.tx ?? 1;
    const uy = a.ty ?? 0;
    const jig = Math.sin(this.h.world.time * 9 + e.id) * 0.6;
    const s = speed * dt;
    // Never too far from home: past twice its roam it turns back round.
    const far = Math.hypot(e.x - a.hx, e.y - a.hy) > ANIMALS[e.critter!.kind as AnimalKind].roam * 3 + 4;
    const k = far ? -1 : 1;
    if (this.h.move(e, (ux - uy * jig) * s * k, (uy + ux * jig) * s * k)) e.moving = true;
    else {
      // Cornered: try the other way round the obstacle.
      [a.tx, a.ty] = [-uy, ux];
    }
    if (a.t <= 0) {
      a.act = 'idle';
      a.t = 0.6;
      a.tx = a.ty = null;
    }
  }

  /** The nearest fight within `r`, if any. */
  private nearest(e: Entity, r: number): Fight | null {
    let best: Fight | null = null;
    let bd = r;
    for (const f of this.fights) {
      const d = Math.hypot(f.x - e.x, f.y - e.y);
      if (d < bd) [best, bd] = [f, d];
    }
    return best;
  }

  /** A dog with the player near: follows (friendly) or keeps off barking (wary). True while it is busy so. */
  private dog(e: Entity, a: AnimalMind, walk: number, run: number, dt: number): boolean {
    const w = this.h.world;
    const p = w.player;
    const d = Math.hypot(p.x - e.x, p.y - e.y);
    const fromHome = Math.hypot(p.x - a.hx, p.y - a.hy);
    if (a.follow !== null) a.followT -= dt;
    const done = p.dead || d > 16 || fromHome > 22 || (a.follow !== null && a.followT <= 0);
    if (a.follow !== null && done) {
      a.follow = null;
      a.restT = 40 + this.rand(e, a) * 40;
      a.act = 'idle';
      a.t = 0;
      return false;
    }
    if (a.follow === null) {
      if (a.restT > 0 || p.dead || d > 7 || w.dayPart === 'night') return false;
      a.follow = p.id;
      a.followT = 25 + this.rand(e, a) * 35;
      a.callT = 0;
    }
    const wary = dogTemper(w.progress.karma, townScore(w)) === 'wary';
    const keep = wary ? 4.5 : 1.6;
    if (d > keep + (wary ? 0.5 : 2.5)) {
      // Catch up: trotting when well behind.
      const tx = p.x + ((e.x - p.x) / d) * keep;
      const ty = p.y + ((e.y - p.y) / d) * keep;
      this.goTo(e, a, tx, ty, d > 5 ? run * 0.8 : walk * 1.3, dt, 0.2);
      return true;
    }
    if (wary && d < keep - 0.8) {
      this.flee(e, a, p, walk * 1.5, dt, 0.4);
      return true;
    }
    // Near enough: face the player, bark now and then (a wary dog often).
    this.faceTo(e, p);
    if (a.callT <= 0) {
      a.callT = (wary ? 1.6 : 6) * (0.6 + this.rand(e, a));
      this.still(e, a, 'call', wary ? 1.2 : 0.9);
      return true;
    }
    if (a.act === 'call' && a.t > 0) return true;
    this.still(e, a, wary ? 'idle' : 'sit', 1);
    return true;
  }

  /** A chick off after the nearest hen when it has strayed; true while it hurries back. */
  private toHen(e: Entity, a: AnimalMind, walk: number, run: number, dt: number): boolean {
    let hen: Entity | null = null;
    let bd = 6;
    for (const o of this.h.world.entities.values()) {
      if (o.critter?.kind !== 'chicken') continue;
      const d = Math.hypot(o.x - e.x, o.y - e.y);
      if (d < bd) [hen, bd] = [o, d];
    }
    if (!hen || bd < 1.1) return false;
    // A spot just beside her, its own for each chick.
    const ang = (e.id * 1.7) % (Math.PI * 2);
    const tx = hen.x + Math.cos(ang) * 0.5;
    const ty = hen.y + Math.sin(ang) * 0.5;
    return this.goTo(e, a, tx, ty, bd > 2.5 ? run * 0.7 : walk * 1.2, dt, 0.3);
  }

  private faceTo(e: Entity, t: { x: number; y: number }): void {
    this.h.face(e, t);
    const dx = t.x - e.x;
    const dy = t.y - e.y;
    const l = Math.hypot(dx, dy);
    if (l > 0.01) {
      e.dirX = dx / l;
      e.dirY = dy / l;
    }
  }
}
